import type { AxiosError } from 'axios';
import omit from 'lodash/omit';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import * as SavedFiltersAPI from 'Services/api/savedFilters';
import { clearError, setError } from 'Stores/FilterStore/FilterStoreHelpers';
import type { SavedFilter } from 'Types/savedFilter';
import { INTENT_TYPES } from 'Utilities/FormBase/FormBase.helpers';
import { getAxiosResponseStatus, isDev } from 'Utilities/helpers';

import {
	COULD_NOT_CREATE_SAVED_SEARCH,
	COULD_NOT_DELETE_SAVED_SEARCH,
	COULD_NOT_LOAD_SAVED_SEARCHES,
	COULD_NOT_UPDATE_SAVED_SEARCH,
	MAX_NUMBER_OF_ALLOWED_SEARCHES_REACHED,
	OMITTED_FIELDS,
} from './SavedFilters.conf';
import { filterExists } from './SavedFilters.helpers';
import type { SavedFiltersStore } from './SavedFiltersStore.types';

const savedFiltersStore = devtools<SavedFiltersStore>(
	(set, get) => ({
		clearError: clearError(set),
		createSavedFilter: async ({ filterData, ...rest }) => {
			// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
			const { filters, setError } = get();

			try {
				const { filter, totalCount } = await SavedFiltersAPI.createSavedFilter({
					payload: {
						filterData: omit(filterData, OMITTED_FIELDS),
						...rest,
					},
				});

				const { id: currentFilter } = filter;

				const alreadyExists = filterExists(filters, currentFilter);

				set(
					() => ({
						currentFilter,
						/**
						 * V1 needs the new filter to be inserted into the store.
						 * V2 navigates back to the list and refetches all saved filters.
						 * This causes a race condition between the fetch insertion and the
						 * creation insertion.
						 * Check if the filter doesn't exist before inserting.
						 * This line can be removed when V1 is removed.
						 */
						filters: alreadyExists ? filters : [filter, ...filters],
						totalCount,
					}),
					false,
					{ filterData, ...rest, type: 'createSavedFilter' },
				);
			} catch (err) {
				const status = getAxiosResponseStatus(err as AxiosError);
				switch (status) {
					case 500:
						setError({ message: COULD_NOT_CREATE_SAVED_SEARCH });
						break;
					case 422:
						setError({ intent: INTENT_TYPES.WARNING, message: MAX_NUMBER_OF_ALLOWED_SEARCHES_REACHED });
						break;
					default:
						setError({ message: err });
				}

				return false;
			}

			return true;
		},
		currentFilter: undefined,
		deleteSavedFilter: async (id, callback) => {
			// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
			const { setError } = get();
			try {
				await SavedFiltersAPI.deleteSavedFilter({ id });
				set(
					(state) => {
						const { currentFilter, filters } = state;
						const nextFilters = filters.filter((filter) => filter.id !== id);
						const nextCurrentFilter = currentFilter === id ? undefined : currentFilter;
						callback?.();
						return {
							currentFilter: nextCurrentFilter,
							filters: nextFilters,
						};
					},
					false,
					{ callback, id, type: 'deleteSavedFilter' },
				);
			} catch (err) {
				setError({ message: COULD_NOT_DELETE_SAVED_SEARCH });
				return false;
			}
			return true;
		},
		error: false,
		filters: [],
		getFilterIdByIndex: (index) => {
			const { filters } = get();
			return filters[index]?.id ? filters[index]?.id : undefined;
		},
		getSavedFilter: (savedFilterId) => {
			const { filters } = get();

			// Legacy fallback: returns an empty object when no match is found. Preserved
			// from the original JS implementation; the cast keeps the public contract as
			// `SavedFilter | undefined`. Safe in practice because callers only pass ids
			// that exist in `filters`.
			return filters?.find((filter) => filter?.id === savedFilterId) || filters?.[0] || ({} as SavedFilter);
		},
		getSavedFilters: async () => {
			// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
			const { setError } = get();

			try {
				const { filters, maxFilters, totalCount } = await SavedFiltersAPI.getSavedFilters();

				set({ filters, maxFilters, totalCount }, false, {
					filters,
					maxFilters,
					totalCount,
					type: 'getSavedFilters',
				});
			} catch (err) {
				setError({ message: COULD_NOT_LOAD_SAVED_SEARCHES });
				return false;
			}

			return true;
		},
		isClickedOutsidePillButton: false,
		isSavedSearchPillOpen: false,
		isUniqueFilter: false,
		maxFilters: undefined,
		setCurrentFilter: (filterId) => {
			set({ currentFilter: filterId }, false, { filterId, type: 'setCurrentFilter' });
		},
		setError: setError(set),
		setFilters: (filters) => {
			set({ filters }, false, { filters, type: 'setFilters' });
		},
		setIsClickedOutsidePillButton: (isClickedOutsidePillButton) => {
			set({ isClickedOutsidePillButton }, false, { isClickedOutsidePillButton, type: 'setIsClickedOutsidePillButton' });
		},
		setIsSavedSearchPillOpen: (isPillOpen) => {
			set({ isSavedSearchPillOpen: isPillOpen }, false, { isPillOpen, type: 'setIsSavedSearchPillOpen' });
		},
		setIsUniqueFilter: (isUniqueFilter) => {
			set({ isUniqueFilter }, false, { type: 'setIsUniqueFilter' });
		},
		totalCount: undefined,
		updateSavedFilter: async ({ filterData, ...rest }, callback) => {
			// eslint-disable-next-line no-shadow, @typescript-eslint/no-shadow
			const { filters: existingFilters, setError } = get();

			try {
				const updatedFilter = await SavedFiltersAPI.updateSavedFilter({
					payload: {
						filterData: omit(filterData, OMITTED_FIELDS),
						...rest,
					},
				});

				const { id } = updatedFilter;
				const filters = existingFilters.map((filter) => (filter?.id === id ? updatedFilter : filter));

				set({ filters }, false, { filterData, ...rest, type: 'updateSavedFilter' });
				callback?.();
			} catch (err) {
				const status = getAxiosResponseStatus(err as AxiosError);
				switch (status) {
					case 500:
						setError({ message: COULD_NOT_UPDATE_SAVED_SEARCH });
						break;
					default:
						setError({ message: err });
				}

				return false;
			}

			return true;
		},
	}),
	{
		enabled: isDev,
		name: 'SavedFiltersStore',
	},
);

const useSavedFiltersStore = create(subscribeWithSelector(savedFiltersStore));

export const useSavedFilters = (): SavedFiltersStore['filters'] => useSavedFiltersStore((store) => store.filters);
export const useCurrentFilter = (): SavedFiltersStore['currentFilter'] =>
	useSavedFiltersStore((store) => store.currentFilter);
export const useIsUniqueFilterInStore = (): SavedFiltersStore['isUniqueFilter'] =>
	useSavedFiltersStore((store) => store.isUniqueFilter);
export const useIsSavedSearchPillOpen = (): SavedFiltersStore['isSavedSearchPillOpen'] =>
	useSavedFiltersStore((store) => store.isSavedSearchPillOpen);
export const useSetCurrentFilter = (): SavedFiltersStore['setCurrentFilter'] =>
	useSavedFiltersStore((store) => store.setCurrentFilter);
export const useSetIsUniqueFilterInStore = (): SavedFiltersStore['setIsUniqueFilter'] =>
	useSavedFiltersStore((store) => store.setIsUniqueFilter);
export const useSetIsSavedSearchPillOpen = (): SavedFiltersStore['setIsSavedSearchPillOpen'] =>
	useSavedFiltersStore((store) => store.setIsSavedSearchPillOpen);
export const useGetSavedFilter = (): SavedFiltersStore['getSavedFilter'] =>
	useSavedFiltersStore((store) => store.getSavedFilter);
export const useGetSavedFilters = (): SavedFiltersStore['getSavedFilters'] =>
	useSavedFiltersStore((store) => store.getSavedFilters);
export const useSetIsClickedOutsidePillButton = (): SavedFiltersStore['setIsClickedOutsidePillButton'] =>
	useSavedFiltersStore((store) => store.setIsClickedOutsidePillButton);
export const useGetFilterIdByIndex = (): SavedFiltersStore['getFilterIdByIndex'] =>
	useSavedFiltersStore((store) => store.getFilterIdByIndex);
export default useSavedFiltersStore;
